import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import {
    Trophy, Zap, FlaskConical, Globe, MapPin, Table2, Users2, Swords,
    Shield, Brain, Clock, RefreshCw, ChevronDown, ChevronUp, Star, Target, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    AreaChart, Area, CartesianGrid, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { ProbBar, StatCard, EngineCard } from '../components/dashboard/SharedComponents';
import OverByOverChart from '../components/dashboard/OverByOverChart';

const TABS = [
    { key: 'verdict', label: 'Final Verdict', icon: Trophy, color: 'text-yellow-400' },
    { key: 'ai_match_lab', label: 'AI Match Lab', icon: Zap, color: 'text-cyan-400' },
    { key: 'impact_player', label: 'Impact Player', icon: Star, color: 'text-orange-400' },
    { key: 'simulation_lab', label: 'Simulation Lab', icon: FlaskConical, color: 'text-purple-400' },
    { key: 'global_prediction', label: 'Global Prediction', icon: Globe, color: 'text-amber-400' },
    { key: 'venue', label: 'Venue', icon: MapPin, color: 'text-green-400' },
    { key: 'standings', label: 'Standings', icon: Table2, color: 'text-blue-400' },
    { key: 'teams', label: 'Teams', icon: Shield, color: 'text-red-400' },
    { key: 'players', label: 'Players', icon: Users2, color: 'text-orange-400' },
    { key: 'matchups', label: 'Matchups', icon: Swords, color: 'text-pink-400' },
    { key: 'god_mode', label: 'God Mode', icon: Brain, color: 'text-violet-400' },
];

export default function VimalDashboard() {
    const [searchParams] = useSearchParams();
    const urlMatchId = searchParams.get('match') || '';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('verdict');
    const [selectedMatchId, setSelectedMatchId] = useState(urlMatchId);
    const [refreshing, setRefreshing] = useState(false);

    const fetchDashboard = useCallback((matchId) => {
        setRefreshing(true);
        const url = matchId ? `/vimal-dashboard?match_id=${matchId}` : '/vimal-dashboard';
        API.get(url).then(res => {
            setData(res.data);
            setLoading(false);
            setRefreshing(false);
        }).catch(() => { setLoading(false); setRefreshing(false); });
    }, []);

    useEffect(() => {
        fetchDashboard(selectedMatchId);
    }, [selectedMatchId, fetchDashboard]);

    // Auto-refresh every 30s when waiting for toss
    useEffect(() => {
        if (data?.status === 'waiting_for_toss') {
            const interval = setInterval(() => fetchDashboard(selectedMatchId), 30000);
            return () => clearInterval(interval);
        }
    }, [data?.status, selectedMatchId, fetchDashboard]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="w-10 h-10 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!data || data.status === 'no_match') {
        return (
            <div className="glass-card p-16 text-center" data-testid="vimal-no-match">
                <Trophy className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <div className="heading-primary text-xl text-white/40">NO MATCHES AVAILABLE</div>
            </div>
        );
    }

    if (data.status === 'waiting_for_toss') {
        return <WaitingForToss data={data} onSelectMatch={setSelectedMatchId} refreshing={refreshing} onRefresh={() => fetchDashboard(selectedMatchId)} />;
    }

    const d = data;
    const tabs = d.tabs;
    const ActiveIcon = TABS.find(t => t.key === activeTab)?.icon || Trophy;

    return (
        <div className="space-y-4" data-testid="vimal-dashboard">
            {/* Header */}
            <div className="glass-card p-5 border-l-4 border-l-yellow-500">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white">VIMAL PREDICTION DASHBOARD</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-white/40">{d.match?.round}</span>
                                <span className="text-xs text-white/20">|</span>
                                <span className="text-xs text-white/40">{d.venue?.name}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="flex items-center gap-2">
                                <span className="heading-primary text-lg" style={{ color: d.team_a?.color }}>{d.team_a?.short_name}</span>
                                <span className="text-white/20 text-xs">VS</span>
                                <span className="heading-primary text-lg" style={{ color: d.team_b?.color }}>{d.team_b?.short_name}</span>
                            </div>
                            <div className="text-[10px] text-white/30 tech-mono">
                                TOSS: {d.toss?.winner?.toUpperCase()} WON | CHOSE TO {d.toss?.decision?.toUpperCase()}
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => fetchDashboard(selectedMatchId)} className="border-white/10 text-white/40 hover:text-white" data-testid="refresh-btn">
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Quick Verdict Bar */}
            <div className="glass-card p-4" data-testid="quick-verdict">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Star className="w-4 h-4 text-yellow-400" />
                        <span className="heading-primary text-sm text-yellow-400">PREDICTED WINNER:</span>
                        <span className="heading-primary text-lg" style={{ color: d.final_verdict?.winner?.color }}>
                            {d.final_verdict?.winner?.name}
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-center">
                            <div className="heading-primary text-2xl neon-text">{d.final_verdict?.team_a_final_pct}%</div>
                            <div className="tech-mono text-[10px] text-white/30">{d.team_a?.short_name}</div>
                        </div>
                        <div className="w-32 md:w-48">
                            <div className="prob-bar h-2">
                                <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${d.final_verdict?.team_a_final_pct}%` }} />
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="heading-primary text-2xl neon-text-pink">{d.final_verdict?.team_b_final_pct}%</div>
                            <div className="tech-mono text-[10px] text-white/30">{d.team_b?.short_name}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="glass-card p-2 overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                    {TABS.map(tab => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.key;
                        return (
                            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                                    ${isActive ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60 hover:bg-white/5'}`}>
                                <Icon className={`w-3.5 h-3.5 ${isActive ? tab.color : ''}`} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            <div data-testid={`tab-content-${activeTab}`}>
                {activeTab === 'verdict' && <VerdictTab data={d} />}
                {activeTab === 'ai_match_lab' && <AIMatchLabTab data={d} />}
                {activeTab === 'impact_player' && <ImpactPlayerTab data={d} />}
                {activeTab === 'simulation_lab' && <SimulationTab data={d} />}
                {activeTab === 'global_prediction' && <GlobalPredictionTab data={d} />}
                {activeTab === 'venue' && <VenueTab data={d} />}
                {activeTab === 'standings' && <StandingsTab data={d} />}
                {activeTab === 'teams' && <TeamsTab data={d} />}
                {activeTab === 'players' && <PlayersTab data={d} />}
                {activeTab === 'matchups' && <MatchupsTab data={d} />}
                {activeTab === 'god_mode' && <GodModeTab data={d} />}
            </div>
        </div>
    );
}

/* ===== WAITING FOR TOSS ===== */
function WaitingForToss({ data, onSelectMatch, refreshing, onRefresh }) {
    const [countdown, setCountdown] = useState('');

    useEffect(() => {
        if (!data.scheduled_time) return;
        const update = () => {
            const diff = new Date(data.scheduled_time) - new Date();
            if (diff <= 0) { setCountdown('MATCH TIME'); return; }
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setCountdown(`${d > 0 ? d + 'd ' : ''}${h}h ${m}m ${s}s`);
        };
        update();
        const iv = setInterval(update, 1000);
        return () => clearInterval(iv);
    }, [data.scheduled_time]);

    const pm = data.pre_match;
    const impactA = pm?.impact_player_predictions?.team_a;
    const impactB = pm?.impact_player_predictions?.team_b;

    return (
        <div className="space-y-4" data-testid="vimal-waiting">
            <div className="glass-card p-5 border-l-4 border-l-yellow-500">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        <h1 className="heading-primary text-base md:text-xl text-white">VIMAL PREDICTION DASHBOARD</h1>
                    </div>
                    <Button onClick={onRefresh} variant="outline" size="sm" className="border-white/10 text-white/40 hover:text-white" data-testid="manual-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* Countdown + Match Info */}
            <div className="glass-card p-8 text-center border border-yellow-500/20 relative overflow-hidden" data-testid="countdown-card">
                <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, #eab30880 0%, transparent 70%)' }} />
                <div className="relative">
                    <div className="tech-mono text-[10px] text-yellow-400/60 tracking-widest mb-3">MATCH COUNTDOWN</div>
                    <div className="flex items-center justify-center gap-8 mb-4">
                        <div className="text-center">
                            <div className="heading-primary text-2xl md:text-3xl" style={{ color: data.team_a?.color }}>{data.team_a?.short_name}</div>
                        </div>
                        <div className="text-center">
                            <div className="heading-primary text-3xl md:text-5xl text-yellow-400 tracking-wider" data-testid="countdown-timer" style={{ textShadow: '0 0 30px rgba(234,179,8,0.3)' }}>
                                {countdown || 'LOADING...'}
                            </div>
                            <div className="text-[10px] text-white/20 tech-mono mt-1">UNTIL TOSS</div>
                        </div>
                        <div className="text-center">
                            <div className="heading-primary text-2xl md:text-3xl" style={{ color: data.team_b?.color }}>{data.team_b?.short_name}</div>
                        </div>
                    </div>
                    <div className="text-sm text-white/40">{data.venue?.name}, {data.venue?.city}</div>
                    <div className="text-xs text-white/20 mt-1">{data.scheduled_time ? new Date(data.scheduled_time).toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                    <div className="mt-3 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full inline-block">
                        <span className="text-[10px] text-yellow-400 tech-mono">ALL 20 AI ENGINES + INTELLIGENCE PIPELINE WILL AUTO-ACTIVATE AFTER TOSS</span>
                    </div>
                </div>
            </div>

            {/* Pre-match Elo + Impact Player Predictions */}
            {pm && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Elo Ratings */}
                    <div className="glass-card p-5" data-testid="pre-match-elo">
                        <div className="flex items-center gap-2 mb-3">
                            <Target className="w-4 h-4 text-cyan-400" />
                            <h3 className="heading-primary text-sm text-white">PRE-MATCH ELO RATINGS</h3>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                            <div className="text-center flex-1">
                                <div className="heading-primary text-3xl" style={{ color: data.team_a?.color }}>{pm.elo_a}</div>
                                <div className="text-xs text-white/30">{data.team_a?.short_name}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-sm text-white/20">VS</div>
                                <div className="text-[10px] text-yellow-400 mt-1">
                                    {pm.elo_favorite} FAVORED
                                </div>
                            </div>
                            <div className="text-center flex-1">
                                <div className="heading-primary text-3xl" style={{ color: data.team_b?.color }}>{pm.elo_b}</div>
                                <div className="text-xs text-white/30">{data.team_b?.short_name}</div>
                            </div>
                        </div>
                    </div>

                    {/* Impact Player Predictions */}
                    <div className="glass-card p-5" data-testid="pre-match-impact">
                        <div className="flex items-center gap-2 mb-3">
                            <Zap className="w-4 h-4 text-orange-400" />
                            <h3 className="heading-primary text-sm text-white">PREDICTED IMPACT PLAYERS</h3>
                        </div>
                        <div className="space-y-2">
                            {impactA?.recommended && (
                                <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                                    <div>
                                        <div className="text-xs font-bold" style={{ color: data.team_a?.color }}>{data.team_a?.short_name}</div>
                                        <div className="text-sm text-white/80">{impactA.recommended.player_name}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-white/40">{impactA.recommended.impact_role} impact</div>
                                        <div className="text-sm text-green-400">+{impactA.recommended.win_pct_shift}% win</div>
                                    </div>
                                </div>
                            )}
                            {impactB?.recommended && (
                                <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                                    <div>
                                        <div className="text-xs font-bold" style={{ color: data.team_b?.color }}>{data.team_b?.short_name}</div>
                                        <div className="text-sm text-white/80">{impactB.recommended.player_name}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-white/40">{impactB.recommended.impact_role} impact</div>
                                        <div className="text-sm text-green-400">+{impactB.recommended.win_pct_shift}% win</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Impact Player Candidates Detail */}
            {pm && (impactA?.batting_impact_candidates?.length > 0 || impactB?.batting_impact_candidates?.length > 0) && (
                <div className="glass-card p-5" data-testid="impact-candidates">
                    <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-orange-400" />
                        <h3 className="heading-primary text-sm text-white">IMPACT PLAYER SCENARIO PREVIEW</h3>
                        <span className="text-[10px] text-white/20 tech-mono ml-auto">HOW IMPACT SUBSTITUTION COULD SHIFT WIN %</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[{ team: data.team_a, candidates: impactA }, { team: data.team_b, candidates: impactB }].map((side, si) => (
                            <div key={si}>
                                <div className="text-xs font-bold mb-2" style={{ color: side.team?.color }}>{side.team?.short_name} CANDIDATES</div>
                                <div className="space-y-1">
                                    {side.candidates?.batting_impact_candidates?.slice(0, 2).map((c, i) => (
                                        <div key={`bat-${i}`} className="flex items-center justify-between p-1.5 bg-white/5 rounded text-xs">
                                            <span className="text-white/70">{c.player_name}</span>
                                            <span className="text-white/30">bat impact</span>
                                            <span className={c.win_pct_shift > 0 ? 'text-green-400' : 'text-red-400'}>
                                                {c.win_pct_shift > 0 ? '+' : ''}{c.win_pct_shift}%
                                            </span>
                                        </div>
                                    ))}
                                    {side.candidates?.bowling_impact_candidates?.slice(0, 2).map((c, i) => (
                                        <div key={`bowl-${i}`} className="flex items-center justify-between p-1.5 bg-white/5 rounded text-xs">
                                            <span className="text-white/70">{c.player_name}</span>
                                            <span className="text-white/30">bowl impact</span>
                                            <span className={c.win_pct_shift > 0 ? 'text-green-400' : 'text-red-400'}>
                                                {c.win_pct_shift > 0 ? '+' : ''}{c.win_pct_shift}%
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Match Selector */}
            {data.available_matches?.length > 0 && (
                <div className="glass-card p-5">
                    <div className="text-xs text-white/30 tech-mono mb-3">OR SELECT ANY MATCH</div>
                    <Select onValueChange={onSelectMatch}>
                        <SelectTrigger data-testid="vimal-match-selector" className="bg-black/50 border-white/10 text-white h-12 max-w-lg">
                            <SelectValue placeholder="Browse all IPL 2026 matches..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10 max-h-64">
                            {data.available_matches.map(m => (
                                <SelectItem key={m.id} value={m.id} className="text-white hover:bg-white/10">
                                    {m.home_team?.short_name} vs {m.away_team?.short_name} — {m.venue?.city} ({m.status}{m.has_toss ? ' | TOSS' : ''})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );
}

/* ===== FINAL VERDICT TAB ===== */
function VerdictTab({ data }) {
    const v = data.final_verdict;
    const tabs = data.tabs;
    return (
        <div className="space-y-4">
            {/* Winner Announcement */}
            <div className="glass-card p-8 text-center border border-yellow-500/30" data-testid="winner-announcement">
                <div className="tech-mono text-[10px] text-white/30 mb-2">VIMAL AI PREDICTION ENGINE — MATCH WINNER</div>
                <div className="heading-primary text-4xl md:text-6xl mb-3" style={{ color: v.winner?.color, textShadow: `0 0 30px ${v.winner?.color}40` }}>
                    {v.winner?.name}
                </div>
                <div className="heading-primary text-5xl md:text-7xl text-yellow-400 mb-2" style={{ textShadow: '0 0 30px rgba(234,179,8,0.4)' }}>
                    {v.winner_pct}%
                </div>
                <div className="text-sm text-white/40 mb-4">{v.verdict_text}</div>
                <div className="text-xs text-white/20">{v.toss_impact}</div>
                <div className="flex items-center justify-center gap-2 mt-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${v.confidence >= 0.7 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        CONFIDENCE: {(v.confidence * 100).toFixed(0)}%
                    </div>
                </div>
            </div>

            {/* All Engine Results Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">

            {/* Over-by-Over Chart */}
            <div className="col-span-2 md:col-span-3">
                <OverByOverChart matchId={data.match?.id} />
            </div>
                <EngineCard title="AI MATCH LAB" pctA={tabs.ai_match_lab?.team_a_win_pct} pctB={tabs.ai_match_lab?.team_b_win_pct} teamA={data.team_a?.short_name} teamB={data.team_b?.short_name} />
                <EngineCard title="MONTE CARLO" pctA={tabs.simulation_lab?.team_a_win_pct} pctB={tabs.simulation_lab?.team_b_win_pct} teamA={data.team_a?.short_name} teamB={data.team_b?.short_name} />
                {tabs.global_prediction?.models?.slice(0, 4).map((m, i) => (
                    <EngineCard key={i} title={m.source.toUpperCase()} pctA={m.team_a_pct} pctB={m.team_b_pct} teamA={data.team_a?.short_name} teamB={data.team_b?.short_name} />
                ))}
            </div>

            {/* Team Comparison */}
            <div className="grid grid-cols-3 gap-4 items-center">
                <div className="glass-card p-6 text-center">
                    <div className="heading-primary text-3xl" style={{ color: data.team_a?.color }}>{v.team_a_final_pct}%</div>
                    <div className="heading-primary text-lg text-white mt-1">{data.team_a?.short_name}</div>
                    <div className="tech-mono text-[10px] text-white/30 mt-1">STR: {tabs.teams?.team_a?.overall_strength}</div>
                </div>
                <div className="text-center">
                    <div className="text-white/20 text-sm">VS</div>
                    <div className="prob-bar h-3 mt-2 mb-2">
                        <div className="h-full rounded-full transition-all" style={{ width: `${v.team_a_final_pct}%`, background: `linear-gradient(to right, ${data.team_a?.color}, ${data.team_a?.color}80)` }} />
                    </div>
                    <div className="text-[10px] text-white/20 tech-mono">TOSS: {data.toss?.winner?.toUpperCase()}</div>
                </div>
                <div className="glass-card p-6 text-center">
                    <div className="heading-primary text-3xl" style={{ color: data.team_b?.color }}>{v.team_b_final_pct}%</div>
                    <div className="heading-primary text-lg text-white mt-1">{data.team_b?.short_name}</div>
                    <div className="tech-mono text-[10px] text-white/30 mt-1">STR: {tabs.teams?.team_b?.overall_strength}</div>
                </div>
            </div>
        </div>
    );
}

/* EngineCard imported from SharedComponents */

/* ===== AI MATCH LAB TAB ===== */
function AIMatchLabTab({ data }) {
    const lab = data.tabs.ai_match_lab;
    const matchDate = data.match?.date ? new Date(data.match.date) : null;
    const impactScenarios = data.impact_player?.scenarios;
    return (
        <div className="space-y-4">
            {/* Match Date/Time */}
            {matchDate && (
                <div className="glass-card p-3 flex items-center justify-between" data-testid="match-datetime">
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm text-white/70">{matchDate.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="text-sm text-cyan-400 font-bold">{matchDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="text-[10px] text-white/30 tech-mono">{data.match?.round || 'IPL 2026'} | {data.match?.status?.toUpperCase()}</div>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-6" data-testid="ai-match-probability">
                    <h3 className="heading-primary text-sm text-white mb-4">WIN PROBABILITY</h3>
                    <div className="space-y-4">
                        <ProbBar label={data.team_a?.short_name} pct={lab.team_a_win_pct} color="bg-cyan-500" textColor="text-cyan-400" />
                        <ProbBar label={data.team_b?.short_name} pct={lab.team_b_win_pct} color="bg-pink-500" textColor="text-pink-400" />
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/5 flex justify-between">
                        <div className="text-center">
                            <div className="tech-mono text-[10px] text-white/30">CONFIDENCE</div>
                            <div className="heading-primary text-2xl text-yellow-400">{(lab.confidence * 100).toFixed(0)}%</div>
                        </div>
                        <div className="text-center">
                            <div className="tech-mono text-[10px] text-cyan-400/50">ELO {data.team_a?.short_name}</div>
                            <div className="text-lg text-cyan-400 font-bold">{lab.team_a_elo}</div>
                        </div>
                        <div className="text-center">
                            <div className="tech-mono text-[10px] text-pink-400/50">ELO {data.team_b?.short_name}</div>
                            <div className="text-lg text-pink-400 font-bold">{lab.team_b_elo}</div>
                        </div>
                    </div>
                </div>
                <div className="glass-card p-6">
                    <h3 className="heading-primary text-sm text-white mb-4">KEY PLAYERS</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="tech-mono text-[10px] text-cyan-400 mb-2">{data.team_a?.short_name}</div>
                            {lab.team_a_key_players?.map((p, i) => (
                                <div key={i} className="flex justify-between py-1 text-sm">
                                    <span className="text-white/70">{p.name}</span>
                                    <span className="text-cyan-400 font-bold">{p.impact}</span>
                                </div>
                            ))}
                        </div>
                        <div>
                            <div className="tech-mono text-[10px] text-pink-400 mb-2">{data.team_b?.short_name}</div>
                            {lab.team_b_key_players?.map((p, i) => (
                                <div key={i} className="flex justify-between py-1 text-sm">
                                    <span className="text-white/70">{p.name}</span>
                                    <span className="text-pink-400 font-bold">{p.impact}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* GPT-5.2 Analysis */}
            {lab.gpt_analysis?.available && (
                <div className="glass-card p-6 border border-purple-500/20" data-testid="gpt-analysis">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="px-2 py-0.5 bg-purple-500/20 rounded text-[10px] text-purple-400 tech-mono font-bold">GPT-5.2 ANALYSIS</div>
                    </div>
                    <div className="text-sm text-white/70 leading-relaxed">{lab.gpt_analysis.reasoning}</div>
                    <div className="mt-3 flex items-center gap-4 text-[10px] text-white/30">
                        <span>Predicted: <span className="text-purple-400 font-bold">{lab.gpt_analysis.raw_winner}</span></span>
                        <span>Confidence: <span className="text-purple-400 font-bold">{lab.gpt_analysis.raw_confidence}%</span></span>
                    </div>
                </div>
            )}

            {/* Engine Breakdown */}
            {lab.engine_breakdown && (
                <div className="glass-card p-6" data-testid="engine-breakdown">
                    <h3 className="heading-primary text-sm text-white mb-4">ENGINE BREAKDOWN</h3>
                    <div className="space-y-2">
                        {Object.entries(lab.engine_breakdown).map(([engine, info]) => (
                            <div key={engine} className="flex items-center gap-3">
                                <div className="w-24 text-[10px] text-white/40 tech-mono uppercase">{engine}</div>
                                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{
                                        width: `${info.pct}%`,
                                        background: info.pct > 55 ? '#00E5FF' : info.pct < 45 ? '#FF2D95' : '#666',
                                    }} />
                                </div>
                                <div className="w-14 text-right text-xs font-bold" style={{
                                    color: info.pct > 55 ? '#00E5FF' : info.pct < 45 ? '#FF2D95' : '#888',
                                }}>{info.pct}%</div>
                                <div className="w-10 text-right text-[9px] text-white/20">{(info.weight * 100).toFixed(0)}%w</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {lab.insights?.length > 0 && (
                <div className="glass-card p-4">
                    <div className="tech-mono text-[10px] text-white/30 mb-2">AI INSIGHTS</div>
                    {lab.insights.map((ins, i) => (
                        <div key={i} className="text-sm text-white/60 py-1 flex items-start gap-2">
                            <TrendingUp className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />{ins}
                        </div>
                    ))}
                </div>
            )}

            {/* Impact Player Scenario in AI Match Lab */}
            {impactScenarios?.best_impact_choice && (
                <div className="glass-card p-5 border border-orange-500/10" data-testid="ai-impact-scenarios">
                    <div className="flex items-center gap-2 mb-3">
                        <Star className="w-4 h-4 text-orange-400" />
                        <h3 className="heading-primary text-sm text-white">IMPACT PLAYER SCENARIO</h3>
                        <span className="text-[10px] text-orange-400/60 tech-mono ml-auto">IF PLAYER X COMES AS IMPACT</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                            { team: data.team_a, best: impactScenarios.best_impact_choice.team_a },
                            { team: data.team_b, best: impactScenarios.best_impact_choice.team_b },
                        ].map((side, i) => side.best && (
                            <div key={i} className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                                <div>
                                    <div className="text-xs font-bold" style={{ color: side.team?.color }}>{side.team?.short_name}</div>
                                    <div className="text-sm text-white/70">{side.best.player_name} ({side.best.impact_role})</div>
                                </div>
                                <div className="text-right">
                                    <div className={`heading-primary text-xl ${side.best.delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {side.best.delta > 0 ? '+' : ''}{side.best.delta}%
                                    </div>
                                    <div className="text-[9px] text-white/30">win% shift</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="text-[10px] text-white/20 tech-mono text-center">ENGINES: {lab.engines_used?.join(' | ')}</div>
        </div>
    );
}

/* ===== IMPACT PLAYER TAB ===== */
function ImpactPlayerTab({ data }) {
    const ip = data.impact_player;
    if (!ip) {
        return (
            <div className="glass-card p-12 text-center" data-testid="impact-no-data">
                <Star className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <div className="heading-primary text-lg text-white/40">IMPACT PLAYER DATA LOADING</div>
                <div className="text-sm text-white/20 mt-2">Impact player analysis will appear once data is available</div>
            </div>
        );
    }

    const scenarios = ip.scenarios;
    const maxSwing = scenarios?.max_swing || 0;

    return (
        <div className="space-y-4" data-testid="impact-player-tab">
            {/* Header */}
            <div className="glass-card p-5 border border-orange-500/20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-orange-400" />
                        <h3 className="heading-primary text-base text-white">IPL IMPACT PLAYER ANALYSIS</h3>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] text-orange-400 tech-mono">MAX WIN% SWING</div>
                        <div className="heading-primary text-xl text-orange-400">{maxSwing}%</div>
                    </div>
                </div>
                <div className="text-xs text-white/30 mt-2">
                    Each team can substitute one player during the match. This analysis shows how different impact player choices could shift the match outcome.
                </div>
            </div>

            {/* Best Impact Choice */}
            {scenarios?.best_impact_choice && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { team: data.team_a, best: scenarios.best_impact_choice.team_a, label: 'A' },
                        { team: data.team_b, best: scenarios.best_impact_choice.team_b, label: 'B' },
                    ].map((side, i) => side.best && (
                        <div key={i} className="glass-card p-5 border-l-4" style={{ borderLeftColor: side.team?.color }} data-testid={`best-impact-${side.label}`}>
                            <div className="text-[10px] tech-mono text-white/30 mb-2">BEST IMPACT CHOICE — {side.team?.short_name}</div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="heading-primary text-lg text-white">{side.best.player_name}</div>
                                    <div className="text-xs text-white/40">{side.best.role} — {side.best.impact_role} impact</div>
                                </div>
                                <div className="text-right">
                                    <div className={`heading-primary text-2xl ${side.best.delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {side.best.delta > 0 ? '+' : ''}{side.best.delta}%
                                    </div>
                                    <div className="text-[10px] text-white/30">win% shift</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-3">
                                <div className="text-center p-1.5 bg-white/5 rounded">
                                    <div className="text-[9px] text-white/30">BAT AVG</div>
                                    <div className="text-sm text-white/80">{side.best.batting_avg}</div>
                                </div>
                                <div className="text-center p-1.5 bg-white/5 rounded">
                                    <div className="text-[9px] text-white/30">SR</div>
                                    <div className="text-sm text-white/80">{side.best.strike_rate}</div>
                                </div>
                                <div className="text-center p-1.5 bg-white/5 rounded">
                                    <div className="text-[9px] text-white/30">ECONOMY</div>
                                    <div className="text-sm text-white/80">{side.best.economy || '-'}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* All Scenarios */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { team: data.team_a, scenarios: scenarios?.team_a_scenarios, label: 'team_a' },
                    { team: data.team_b, scenarios: scenarios?.team_b_scenarios, label: 'team_b' },
                ].map((side, si) => (
                    <div key={si} className="glass-card p-5" data-testid={`impact-scenarios-${side.label}`}>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: side.team?.color }} />
                            <h3 className="heading-primary text-sm text-white">{side.team?.short_name} IMPACT SCENARIOS</h3>
                        </div>
                        <div className="space-y-1.5">
                            {side.scenarios?.map((s, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded text-xs">
                                    <div className="flex-1">
                                        <span className="text-white/70">{s.player_name}</span>
                                        <span className="text-white/20 ml-2">({s.impact_role})</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-white/40">{s.adjusted_win_pct}%</span>
                                        <span className={`font-bold min-w-[50px] text-right ${s.delta > 0 ? 'text-green-400' : s.delta < 0 ? 'text-red-400' : 'text-white/40'}`}>
                                            {s.delta > 0 ? '+' : ''}{s.delta}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                            {(!side.scenarios || side.scenarios.length === 0) && (
                                <div className="text-xs text-white/20 py-2">No scenarios available</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Impact Player Candidates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { team: data.team_a, candidates: ip.team_a_candidates },
                    { team: data.team_b, candidates: ip.team_b_candidates },
                ].map((side, si) => (
                    <div key={si} className="glass-card p-5">
                        <div className="text-[10px] tech-mono text-white/30 mb-2">{side.team?.short_name} — BATTING vs BOWLING IMPACT</div>
                        <div className="space-y-2">
                            {side.candidates?.batting_impact_candidates?.map((c, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    <span className="text-white/60 flex-1">{c.player_name}</span>
                                    <span className="text-white/30">BAT</span>
                                    <span className="text-green-400">+{c.win_pct_shift}%</span>
                                </div>
                            ))}
                            {side.candidates?.bowling_impact_candidates?.map((c, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                    <span className="text-white/60 flex-1">{c.player_name}</span>
                                    <span className="text-white/30">BOWL</span>
                                    <span className="text-blue-400">+{c.win_pct_shift}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="text-center text-[10px] text-white/15 tech-mono">
                IPL IMPACT PLAYER RULE — EACH TEAM CAN SUBSTITUTE ONE PLAYER DURING THE MATCH
            </div>
        </div>
    );
}


/* ===== SIMULATION TAB ===== */
function SimulationTab({ data }) {
    const sim = data.tabs.simulation_lab;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label={`${data.team_a?.short_name} WIN`} value={`${sim.team_a_win_pct}%`} color="text-cyan-400" sub={`Avg: ${sim.team_a_avg_score}`} />
                <StatCard label={`${data.team_b?.short_name} WIN`} value={`${sim.team_b_win_pct}%`} color="text-pink-400" sub={`Avg: ${sim.team_b_avg_score}`} />
                <StatCard label="TIE" value={`${sim.tie_pct}%`} color="text-yellow-400" />
                <StatCard label="SIMULATIONS" value={sim.simulations?.toLocaleString()} color="text-white" />
            </div>
            <div className="glass-card p-6" data-testid="sim-distribution">
                <h3 className="heading-primary text-sm text-white mb-4">SCORE DISTRIBUTION</h3>
                <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={sim.distribution || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis dataKey="range" tick={{ fill: '#666', fontSize: 9 }} interval={2} />
                        <YAxis tick={{ fill: '#666', fontSize: 9 }} />
                        <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} />
                        <Area type="monotone" dataKey="team_a" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.2} name={data.team_a?.short_name} />
                        <Area type="monotone" dataKey="team_b" stroke="#FF2D95" fill="#FF2D95" fillOpacity={0.2} name={data.team_b?.short_name} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            {sim.toss_scenarios && (
                <div className="glass-card p-6" data-testid="toss-scenarios">
                    <h3 className="heading-primary text-sm text-white mb-3">TOSS SCENARIO ANALYSIS</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-xs text-white/40 mb-2">{data.team_a?.short_name} bats first</div>
                            <div className="flex justify-between text-sm">
                                <span className="text-cyan-400">{data.team_a?.short_name}: {sim.toss_scenarios.team_a_bats_first?.team_a_win_pct}%</span>
                                <span className="text-pink-400">{data.team_b?.short_name}: {sim.toss_scenarios.team_a_bats_first?.team_b_win_pct}%</span>
                            </div>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-xs text-white/40 mb-2">{data.team_b?.short_name} bats first</div>
                            <div className="flex justify-between text-sm">
                                <span className="text-cyan-400">{data.team_a?.short_name}: {sim.toss_scenarios.team_b_bats_first?.team_a_win_pct}%</span>
                                <span className="text-pink-400">{data.team_b?.short_name}: {sim.toss_scenarios.team_b_bats_first?.team_b_win_pct}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ===== GLOBAL PREDICTION TAB ===== */
function GlobalPredictionTab({ data }) {
    const gp = data.tabs.global_prediction;
    const ext = gp.external || {};
    const aiAnalysis = ext.ai_analysis || {};
    const radarData = gp.models?.map(m => ({
        source: m.source.replace(' Model', '').replace(' Index', ''),
        team_a: m.team_a_pct,
        team_b: m.team_b_pct,
    })) || [];

    const statusColors = { success: 'text-green-400', no_relevant_content: 'text-yellow-400' };
    const statusBg = { success: 'bg-green-500/10', no_relevant_content: 'bg-yellow-500/10' };

    return (
        <div className="space-y-4">
            {/* External Predictions Section */}
            {ext.scraped_sources && (
                <div className="glass-card p-6 border-l-4 border-l-amber-500" data-testid="external-predictions">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-amber-400" />
                            <h3 className="heading-primary text-sm text-white">EXTERNAL INTELLIGENCE</h3>
                            <div className="badge-ai">{aiAnalysis.ai_analyzed ? 'GPT-5.2 ANALYZED' : 'KEYWORD'}</div>
                        </div>
                        <div className="text-[10px] text-white/30 tech-mono">
                            {ext.successful_sources}/{ext.total_sources} SOURCES ACTIVE
                        </div>
                    </div>

                    {/* AI Analysis Result */}
                    {aiAnalysis.external_consensus_team_a_pct && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="p-4 bg-white/5 rounded-lg text-center">
                                <div className="tech-mono text-[10px] text-white/30 mb-1">EXTERNAL CONSENSUS</div>
                                <div className="flex items-center justify-center gap-4">
                                    <div>
                                        <div className="heading-primary text-2xl text-cyan-400">{aiAnalysis.external_consensus_team_a_pct}%</div>
                                        <div className="text-[10px] text-white/30">{data.team_a?.short_name}</div>
                                    </div>
                                    <div className="text-white/20">vs</div>
                                    <div>
                                        <div className="heading-primary text-2xl text-pink-400">{aiAnalysis.external_consensus_team_b_pct}%</div>
                                        <div className="text-[10px] text-white/30">{data.team_b?.short_name}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 bg-white/5 rounded-lg text-center">
                                <div className="tech-mono text-[10px] text-white/30 mb-1">EXPERTS LEAN</div>
                                <div className="heading-primary text-lg text-yellow-400">{aiAnalysis.expert_lean}</div>
                                <div className="text-[10px] text-white/30">Confidence: {((aiAnalysis.confidence || 0.5) * 100).toFixed(0)}%</div>
                            </div>
                            <div className="p-4 bg-white/5 rounded-lg">
                                <div className="tech-mono text-[10px] text-white/30 mb-1">SENTIMENT</div>
                                <div className="flex justify-between text-sm">
                                    <span className={aiAnalysis.sentiment_team_a === 'positive' ? 'text-green-400' : aiAnalysis.sentiment_team_a === 'negative' ? 'text-red-400' : 'text-white/50'}>
                                        {data.team_a?.short_name}: {aiAnalysis.sentiment_team_a}
                                    </span>
                                    <span className={aiAnalysis.sentiment_team_b === 'positive' ? 'text-green-400' : aiAnalysis.sentiment_team_b === 'negative' ? 'text-red-400' : 'text-white/50'}>
                                        {data.team_b?.short_name}: {aiAnalysis.sentiment_team_b}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* AI Insights */}
                    {aiAnalysis.key_insights?.length > 0 && (
                        <div className="p-3 bg-white/5 rounded-lg mb-4">
                            <div className="tech-mono text-[10px] text-white/30 mb-2">AI INSIGHTS FROM EXTERNAL DATA</div>
                            {aiAnalysis.key_insights.map((ins, i) => (
                                <div key={i} className="text-sm text-white/60 py-0.5 flex items-start gap-2">
                                    <TrendingUp className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />{ins}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Source-level breakdown */}
                    {aiAnalysis.source_breakdown?.length > 0 && (
                        <div className="p-3 bg-white/5 rounded-lg mb-4">
                            <div className="tech-mono text-[10px] text-white/30 mb-2">SOURCE BREAKDOWN</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {aiAnalysis.source_breakdown.map((sb, i) => (
                                    <div key={i} className="p-2 bg-white/5 rounded text-center">
                                        <div className="text-[10px] text-white/40">{sb.source}</div>
                                        <div className={`text-sm font-bold ${sb.prediction?.toLowerCase().includes(data.team_a?.short_name?.toLowerCase()) ? 'text-cyan-400' : 'text-pink-400'}`}>
                                            {sb.prediction}
                                        </div>
                                        <div className="text-[9px] text-white/20">{((sb.confidence || 0.5) * 100).toFixed(0)}% conf</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Scraped Sources Status */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {ext.scraped_sources?.map((s, i) => (
                            <div key={i} className={`p-2 rounded-lg ${statusBg[s.status] || 'bg-red-500/10'}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-white/70">{s.source}</span>
                                    <span className={`text-[9px] ${statusColors[s.status] || 'text-red-400'}`}>
                                        {s.status === 'success' ? 'LIVE' : s.status === 'no_relevant_content' ? 'NO DATA' : 'ERROR'}
                                    </span>
                                </div>
                                {s.predictions?.length > 0 && (
                                    <div className="text-[9px] text-white/30 mt-1 line-clamp-2">{s.predictions[0]}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Internal Models */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="glass-card p-6" data-testid="model-radar">
                    <h3 className="heading-primary text-sm text-white mb-4">INTERNAL MODEL COMPARISON</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <RadarChart data={radarData}>
                            <PolarGrid stroke="#333" />
                            <PolarAngleAxis dataKey="source" tick={{ fill: '#888', fontSize: 9 }} />
                            <PolarRadiusAxis tick={{ fill: '#555', fontSize: 8 }} domain={[0, 100]} />
                            <Radar name={data.team_a?.short_name} dataKey="team_a" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.15} />
                            <Radar name={data.team_b?.short_name} dataKey="team_b" stroke="#FF2D95" fill="#FF2D95" fillOpacity={0.15} />
                            <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <div className="glass-card p-6 max-h-[360px] overflow-y-auto">
                    <h3 className="heading-primary text-sm text-white mb-4">ALL MODELS ({gp.models?.length})</h3>
                    <div className="space-y-2">
                        {gp.models?.map((m, i) => (
                            <div key={i} className="p-3 bg-white/5 rounded-lg">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-white">{m.source}</span>
                                    <span className="text-xs text-white/30">Reliability: {(m.reliability * 100).toFixed(0)}%</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-cyan-400 w-10">{m.team_a_pct}%</span>
                                    <div className="flex-1 prob-bar h-1.5">
                                        <div className="h-full rounded-full bg-cyan-500" style={{ width: `${m.team_a_pct}%` }} />
                                    </div>
                                    <span className="text-xs text-pink-400 w-10 text-right">{m.team_b_pct}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ===== VENUE TAB ===== */
function VenueTab({ data }) {
    const v = data.tabs.venue;
    return (
        <div className="space-y-4">
            <div className="glass-card p-6" data-testid="venue-info">
                <h3 className="heading-primary text-sm text-white mb-4">{v.venue?.name}, {v.venue?.city}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label="AVG 1ST INN" value={v.venue?.avg_first_innings} color="text-cyan-400" />
                    <StatCard label="AVG 2ND INN" value={v.venue?.avg_second_innings} color="text-green-400" />
                    <StatCard label="HOME WIN %" value={`${v.home_win_pct}%`} color="text-yellow-400" />
                    <StatCard label="MATCHES" value={v.venue_total_matches} color="text-white" />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-6">
                    <div className="tech-mono text-[10px] text-cyan-400 mb-2">{data.team_a?.short_name} AT VENUE</div>
                    <div className="grid grid-cols-3 gap-2">
                        <StatCard label="PLAYED" value={v.team_a_venue?.played || 0} color="text-white" />
                        <StatCard label="WON" value={v.team_a_venue?.won || 0} color="text-green-400" />
                        <StatCard label="WIN %" value={`${((v.team_a_venue?.win_rate || 0) * 100).toFixed(0)}%`} color="text-cyan-400" />
                    </div>
                </div>
                <div className="glass-card p-6">
                    <div className="tech-mono text-[10px] text-pink-400 mb-2">{data.team_b?.short_name} AT VENUE</div>
                    <div className="grid grid-cols-3 gap-2">
                        <StatCard label="PLAYED" value={v.team_b_venue?.played || 0} color="text-white" />
                        <StatCard label="WON" value={v.team_b_venue?.won || 0} color="text-green-400" />
                        <StatCard label="WIN %" value={`${((v.team_b_venue?.win_rate || 0) * 100).toFixed(0)}%`} color="text-pink-400" />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ===== STANDINGS TAB ===== */
function StandingsTab({ data }) {
    const standings = data.tabs.standings || [];
    return (
        <div className="glass-card p-6" data-testid="standings-table">
            <h3 className="heading-primary text-sm text-white mb-4">IPL STANDINGS</h3>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-white/30 text-xs tech-mono border-b border-white/5">
                            <th className="text-left py-2 px-2">#</th><th className="text-left py-2">TEAM</th>
                            <th className="text-center py-2">P</th><th className="text-center py-2">W</th>
                            <th className="text-center py-2">L</th><th className="text-center py-2">PTS</th>
                            <th className="text-center py-2">NRR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {standings.map((s, i) => {
                            const isMatch = s.team_id === data.team_a?.id || s.team_id === data.team_b?.id;
                            return (
                                <tr key={i} className={`border-b border-white/5 ${isMatch ? 'bg-white/5' : ''}`}>
                                    <td className="py-2 px-2 text-white/40">{s.position}</td>
                                    <td className="py-2 font-medium" style={{ color: isMatch ? (s.team_id === data.team_a?.id ? data.team_a?.color : data.team_b?.color) : '#999' }}>
                                        {s.short_name}
                                    </td>
                                    <td className="py-2 text-center text-white/60">{s.played}</td>
                                    <td className="py-2 text-center text-green-400">{s.won}</td>
                                    <td className="py-2 text-center text-red-400">{s.lost}</td>
                                    <td className="py-2 text-center text-yellow-400 font-bold">{s.points}</td>
                                    <td className="py-2 text-center text-white/40">{s.nrr > 0 ? '+' : ''}{s.nrr}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ===== TEAMS TAB ===== */
function TeamsTab({ data }) {
    const t = data.tabs.teams;
    const compares = [
        { label: 'BATTING', a: t.team_a?.batting_strength, b: t.team_b?.batting_strength },
        { label: 'BOWLING', a: t.team_a?.bowling_strength, b: t.team_b?.bowling_strength },
        { label: 'OVERALL', a: t.team_a?.overall_strength, b: t.team_b?.overall_strength },
        { label: 'WIN RATE', a: t.team_a?.win_rate, b: t.team_b?.win_rate },
    ];
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 items-center">
                <div className="glass-card p-6 text-center">
                    <div className="heading-primary text-2xl" style={{ color: data.team_a?.color }}>{data.team_a?.short_name}</div>
                    <div className="heading-primary text-4xl text-white mt-2">{t.team_a?.overall_strength}</div>
                    <div className="tech-mono text-[10px] text-white/30">OVERALL RATING</div>
                    <div className="text-xs text-white/20 mt-2">{t.team_a?.titles} titles</div>
                </div>
                <div className="space-y-3">
                    {compares.map((c, i) => (
                        <div key={i}>
                            <div className="text-[10px] text-white/30 tech-mono text-center mb-1">{c.label}</div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-cyan-400 w-8 text-right">{c.a}%</span>
                                <div className="flex-1 flex h-2 bg-white/5 rounded-full overflow-hidden">
                                    <div className="h-full bg-cyan-500 rounded-l-full" style={{ width: `${c.a / (c.a + c.b) * 100}%` }} />
                                    <div className="h-full bg-pink-500 flex-1 rounded-r-full" />
                                </div>
                                <span className="text-xs text-pink-400 w-8">{c.b}%</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="glass-card p-6 text-center">
                    <div className="heading-primary text-2xl" style={{ color: data.team_b?.color }}>{data.team_b?.short_name}</div>
                    <div className="heading-primary text-4xl text-white mt-2">{t.team_b?.overall_strength}</div>
                    <div className="tech-mono text-[10px] text-white/30">OVERALL RATING</div>
                    <div className="text-xs text-white/20 mt-2">{t.team_b?.titles} titles</div>
                </div>
            </div>
        </div>
    );
}

/* ===== PLAYERS TAB ===== */
function PlayersTab({ data }) {
    const p = data.tabs.players;
    const playerNameMap = {};
    [...(p.team_a_xi || []), ...(p.team_b_xi || [])].forEach(pl => {
        if (pl.id) playerNameMap[String(pl.id)] = pl.name;
    });
    return (
        <div className="space-y-4">
            {!p.has_real_lineup && (
                <div className="glass-card p-3 border border-yellow-500/20 text-center">
                    <div className="text-xs text-yellow-400">Playing XI estimated from squad — real lineup will load after toss</div>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-6" data-testid="team-a-xi">
                    <div className="flex items-center justify-between mb-3">
                        <div className="heading-primary text-sm" style={{ color: data.team_a?.color }}>{data.team_a?.short_name} XI</div>
                        <div className="tech-mono text-[10px] text-white/30">IMPACT: {p.total_impact_a}</div>
                    </div>
                    <div className="space-y-1">
                        {p.team_a_xi?.map((pl, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white/5 rounded">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-white/20 w-4">{i + 1}</span>
                                    <span className="text-sm text-white/80">{pl.name}</span>
                                    {pl.is_captain && <span className="text-[9px] text-yellow-400 font-bold">C</span>}
                                    {pl.is_wicketkeeper && <span className="text-[9px] text-cyan-400 font-bold">WK</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-white/30">{pl.role || pl.position}</span>
                                    <span className="text-sm font-bold text-cyan-400">{pl.impact_score}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="glass-card p-6" data-testid="team-b-xi">
                    <div className="flex items-center justify-between mb-3">
                        <div className="heading-primary text-sm" style={{ color: data.team_b?.color }}>{data.team_b?.short_name} XI</div>
                        <div className="tech-mono text-[10px] text-white/30">IMPACT: {p.total_impact_b}</div>
                    </div>
                    <div className="space-y-1">
                        {p.team_b_xi?.map((pl, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white/5 rounded">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-white/20 w-4">{i + 1}</span>
                                    <span className="text-sm text-white/80">{pl.name}</span>
                                    {pl.is_captain && <span className="text-[9px] text-yellow-400 font-bold">C</span>}
                                    {pl.is_wicketkeeper && <span className="text-[9px] text-cyan-400 font-bold">WK</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-white/30">{pl.role || pl.position}</span>
                                    <span className="text-sm font-bold text-pink-400">{pl.impact_score}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Match Batting Scorecard */}
            {p.match_batting?.length > 0 && (
                <div className="glass-card p-6" data-testid="match-batting">
                    <h3 className="heading-primary text-sm text-white mb-3">MATCH BATTING SCORECARD</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-white/30 text-[10px] tech-mono border-b border-white/5">
                                    <th className="text-left py-2">BATTER</th>
                                    <th className="text-center py-2">R</th>
                                    <th className="text-center py-2">B</th>
                                    <th className="text-center py-2">4s</th>
                                    <th className="text-center py-2">6s</th>
                                    <th className="text-center py-2">SR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {p.match_batting.map((b, i) => (
                                    <tr key={i} className="border-b border-white/5">
                                        <td className="py-1.5 text-white/70">{b.player_name || playerNameMap[b.player_id] || b.player_id}</td>
                                        <td className={`py-1.5 text-center font-bold ${b.runs >= 50 ? 'text-yellow-400' : b.runs >= 30 ? 'text-green-400' : 'text-white/60'}`}>{b.runs}</td>
                                        <td className="py-1.5 text-center text-white/40">{b.balls}</td>
                                        <td className="py-1.5 text-center text-white/40">{b.fours}</td>
                                        <td className="py-1.5 text-center text-white/40">{b.sixes}</td>
                                        <td className={`py-1.5 text-center ${b.strike_rate >= 150 ? 'text-green-400' : b.strike_rate >= 100 ? 'text-white/60' : 'text-red-400'}`}>{b.strike_rate}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Match Bowling Scorecard */}
            {p.match_bowling?.length > 0 && (
                <div className="glass-card p-6" data-testid="match-bowling">
                    <h3 className="heading-primary text-sm text-white mb-3">MATCH BOWLING SCORECARD</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-white/30 text-[10px] tech-mono border-b border-white/5">
                                    <th className="text-left py-2">BOWLER</th>
                                    <th className="text-center py-2">O</th>
                                    <th className="text-center py-2">R</th>
                                    <th className="text-center py-2">W</th>
                                    <th className="text-center py-2">ECO</th>
                                    <th className="text-center py-2">M</th>
                                </tr>
                            </thead>
                            <tbody>
                                {p.match_bowling.map((b, i) => (
                                    <tr key={i} className="border-b border-white/5">
                                        <td className="py-1.5 text-white/70">{b.player_name || playerNameMap[b.player_id] || b.player_id}</td>
                                        <td className="py-1.5 text-center text-white/60">{b.overs}</td>
                                        <td className="py-1.5 text-center text-white/60">{b.runs}</td>
                                        <td className={`py-1.5 text-center font-bold ${b.wickets >= 3 ? 'text-yellow-400' : b.wickets >= 2 ? 'text-green-400' : 'text-white/60'}`}>{b.wickets}</td>
                                        <td className={`py-1.5 text-center ${b.economy <= 6 ? 'text-green-400' : b.economy >= 10 ? 'text-red-400' : 'text-white/60'}`}>{b.economy}</td>
                                        <td className="py-1.5 text-center text-white/40">{b.maidens}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
function MatchupsTab({ data }) {
    const matchups = data.tabs.matchups?.matchups || [];
    return (
        <div className="glass-card p-6" data-testid="matchups-list">
            <h3 className="heading-primary text-sm text-white mb-4">KEY PLAYER MATCHUPS</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {matchups.map((m, i) => (
                    <div key={i} className="p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white">{m.batter} vs {m.bowler}</span>
                            <span className={`text-xs font-bold ${m.advantage === 'Batter' ? 'text-green-400' : 'text-red-400'}`}>
                                {m.advantage} +{m.advantage_percentage}%
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-cyan-400">BAT: {m.batter_score}</span>
                            <div className="flex-1 prob-bar h-1.5">
                                <div className="h-full rounded-full bg-cyan-500" style={{ width: `${m.batter_score / (m.batter_score + m.bowler_score) * 100}%` }} />
                            </div>
                            <span className="text-[10px] text-pink-400">BOWL: {m.bowler_score}</span>
                        </div>
                        <div className="flex justify-between mt-1 text-[10px] text-white/20">
                            <span>SR: {m.expected_strike_rate}</span>
                            <span>Dismissal: {m.dismissal_probability}%</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ===== GOD MODE TAB — ULTIMATE VERDICT ===== */
function GodModeTab({ data }) {
    const [gm, setGm] = useState(null);
    const [gmLoading, setGmLoading] = useState(true);

    useEffect(() => {
        setGmLoading(true);
        API.get(`/god-mode-verdict?match_id=${data.match?.id || ''}`)
            .then(res => { setGm(res.data); setGmLoading(false); })
            .catch(() => setGmLoading(false));
    }, [data.match?.id]);

    if (gmLoading) {
        return (
            <div className="glass-card p-16 text-center" data-testid="god-mode-loading">
                <div className="w-12 h-12 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <div className="heading-primary text-lg text-yellow-400 mb-2">ACTIVATING GOD MODE</div>
                <div className="text-xs text-white/30 tech-mono">Running all 20 engines + 7 data sources in parallel...</div>
                <div className="text-xs text-white/20 tech-mono mt-1">Feeding intelligence, analytics, and AI into GPT-5.2 Master Reasoning...</div>
            </div>
        );
    }

    if (!gm || gm.status !== 'ready') {
        return (
            <div className="glass-card p-12 text-center" data-testid="god-mode-no-data">
                <Brain className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <div className="heading-primary text-lg text-white/40">GOD MODE UNAVAILABLE</div>
                <div className="text-sm text-white/20 mt-2">{gm?.message || 'Requires toss data to activate'}</div>
            </div>
        );
    }

    const v = gm.verdict;
    const agr = gm.agreement;
    const masterGpt = gm.master_gpt;

    return (
        <div className="space-y-4" data-testid="god-mode-verdict">
            {/* MASSIVE WINNER ANNOUNCEMENT */}
            <div className="glass-card p-8 text-center border-2 border-yellow-500/40 relative overflow-hidden" data-testid="god-mode-winner">
                <div className="absolute inset-0 opacity-5" style={{ background: `radial-gradient(circle at 50% 0%, ${v.winner?.color}80 0%, transparent 70%)` }} />
                <div className="relative">
                    <div className="tech-mono text-[10px] text-yellow-400/60 mb-2 tracking-widest">
                        GOD MODE VERDICT — {gm.all_engines?.length} ENGINES ANALYZED
                    </div>
                    <div className="heading-primary text-4xl sm:text-5xl lg:text-6xl mb-2"
                        style={{ color: v.winner?.color, textShadow: `0 0 40px ${v.winner?.color}50` }}>
                        {v.winner?.name}
                    </div>
                    <div className="heading-primary text-5xl sm:text-6xl lg:text-7xl text-yellow-400 mb-3"
                        style={{ textShadow: '0 0 40px rgba(234,179,8,0.4)' }}>
                        {v.winner_pct}%
                    </div>
                    <div className="text-sm text-white/50 mb-3">{v.verdict_text}</div>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${v.confidence >= 0.7 ? 'bg-green-500/20 text-green-400 border border-green-500/20' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/20'}`}
                            data-testid="god-mode-confidence">
                            CONFIDENCE: {(v.confidence * 100).toFixed(0)}%
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${agr.consensus_level === 'overwhelming' ? 'bg-green-500/20 text-green-400' : agr.consensus_level === 'strong' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-orange-500/20 text-orange-400'} border border-white/5`}
                            data-testid="god-mode-consensus">
                            {agr.agree_count}/{agr.total_engines} ENGINES AGREE
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-400 border border-purple-500/20">
                            {agr.consensus_level?.toUpperCase()} CONSENSUS
                        </span>
                    </div>
                    <div className="text-xs text-white/20 mt-3">{v.toss_impact}</div>
                </div>
            </div>

            {/* Probability bar */}
            <div className="glass-card p-4" data-testid="god-mode-prob-bar">
                <div className="flex items-center justify-between gap-4">
                    <div className="text-center min-w-[60px]">
                        <div className="heading-primary text-2xl" style={{ color: gm.team_a?.color }}>{v.team_a_final_pct}%</div>
                        <div className="tech-mono text-[10px] text-white/30">{gm.team_a?.short_name}</div>
                    </div>
                    <div className="flex-1 h-4 bg-white/5 rounded-full overflow-hidden flex">
                        <div className="h-full rounded-l-full transition-all duration-700" style={{ width: `${v.team_a_final_pct}%`, backgroundColor: gm.team_a?.color }} />
                        <div className="h-full rounded-r-full flex-1 transition-all duration-700" style={{ backgroundColor: gm.team_b?.color }} />
                    </div>
                    <div className="text-center min-w-[60px]">
                        <div className="heading-primary text-2xl" style={{ color: gm.team_b?.color }}>{v.team_b_final_pct}%</div>
                        <div className="tech-mono text-[10px] text-white/30">{gm.team_b?.short_name}</div>
                    </div>
                </div>
            </div>

            {/* HOW GOD MODE WORKS — Transparency Panel */}
            <div className="glass-card p-5 border border-white/5" data-testid="god-mode-explanation">
                <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-4 h-4 text-yellow-400" />
                    <h3 className="heading-primary text-sm text-white">HOW GOD MODE WORKS</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[10px] text-white/40">
                    <div className="bg-white/[0.02] p-3 rounded-lg border border-white/5">
                        <div className="text-cyan-400 font-bold tech-mono mb-1">1. ENGINE AGGREGATION</div>
                        <div>{gm.all_engines?.length || 0} independent AI & statistical engines each produce a win probability. Engines span Elo ratings, Monte Carlo simulations, form analysis, venue intelligence, player matchups, and more.</div>
                    </div>
                    <div className="bg-white/[0.02] p-3 rounded-lg border border-white/5">
                        <div className="text-purple-400 font-bold tech-mono mb-1">2. INTELLIGENCE LAYER</div>
                        <div>Social sentiment from Twitter/X, Reddit, Instagram, YouTube, Facebook, Telegram + Google Trends momentum + community wisdom are scraped and scored. This feeds into {(gm.all_engines || []).filter(e => e.category === 'intelligence').length} intelligence engines.</div>
                    </div>
                    <div className="bg-white/[0.02] p-3 rounded-lg border border-white/5">
                        <div className="text-yellow-400 font-bold tech-mono mb-1">3. GPT-5.2 SYNTHESIS</div>
                        <div>All engine outputs, intelligence data, and match context are synthesized by GPT-5.2 into a unified Master Verdict with key factors and risk assessment. Final % is a weighted combination of all engines.</div>
                    </div>
                </div>
            </div>

            {/* ENGINE AGREEMENT MATRIX */}
            <div className="glass-card p-6" data-testid="engine-agreement-matrix">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-yellow-400" />
                        <h3 className="heading-primary text-sm text-white">ENGINE AGREEMENT MATRIX</h3>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] tech-mono">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> AGREES</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> DISSENTS</span>
                    </div>
                </div>

                {/* Group engines by category */}
                {(() => {
                    const categoryOrder = ['statistical', 'simulation', 'ai', 'form', 'historical', 'venue', 'player', 'batting', 'bowling', 'matchup', 'external', 'intelligence', 'analytics'];
                    const categoryLabels = {
                        statistical: 'STATISTICAL MODELS', simulation: 'SIMULATIONS', ai: 'AI ANALYSIS',
                        form: 'FORM & MOMENTUM', historical: 'HISTORICAL', venue: 'VENUE', player: 'PLAYER',
                        batting: 'BATTING', bowling: 'BOWLING', matchup: 'MATCHUPS', external: 'EXTERNAL',
                        intelligence: 'SOCIAL INTELLIGENCE', analytics: 'ANALYTICS',
                    };
                    const categoryColors = {
                        statistical: 'text-cyan-400', simulation: 'text-purple-400', ai: 'text-violet-400',
                        form: 'text-green-400', historical: 'text-amber-400', venue: 'text-emerald-400',
                        player: 'text-orange-400', batting: 'text-green-300', bowling: 'text-blue-400',
                        matchup: 'text-pink-400', external: 'text-yellow-400',
                        intelligence: 'text-cyan-300', analytics: 'text-teal-400',
                    };
                    const grouped = {};
                    (gm.all_engines || []).forEach(e => {
                        const cat = e.category || 'other';
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push(e);
                    });
                    const sortedCats = categoryOrder.filter(c => grouped[c]);
                    return sortedCats.map(cat => (
                        <div key={cat} className="mb-4">
                            <div className={`text-[10px] tech-mono font-bold mb-2 ${categoryColors[cat] || 'text-white/40'}`}>
                                {categoryLabels[cat] || cat.toUpperCase()} ({grouped[cat].length})
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {grouped[cat].map((e, i) => {
                                    const favors = e.team_a_pct > 50 ? gm.team_a : gm.team_b;
                                    const favorPct = Math.max(e.team_a_pct, e.team_b_pct);
                                    return (
                                        <div key={i} data-testid={`engine-card-${cat}-${i}`}
                                            className={`p-3 rounded-lg border transition-all group ${e.agrees_with_verdict
                                                ? 'border-green-500/20 bg-green-500/5 hover:bg-green-500/10'
                                                : 'border-red-500/20 bg-red-500/5 hover:bg-red-500/10'}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="text-[10px] text-white/50 font-medium leading-tight">{e.engine}</div>
                                                <div className={`w-2 h-2 rounded-full ${e.agrees_with_verdict ? 'bg-green-500' : 'bg-red-500'}`} />
                                            </div>
                                            <div className="heading-primary text-xl" style={{ color: favors?.color }}>
                                                {favorPct}%
                                            </div>
                                            <div className="text-[9px] text-white/30">{favors?.short_name} favored</div>
                                            <div className="prob-bar h-1 mt-1.5">
                                                <div className="h-full rounded-full transition-all" style={{ width: `${e.team_a_pct}%`, backgroundColor: gm.team_a?.color }} />
                                            </div>
                                            <div className="flex justify-between mt-1 text-[8px] text-white/20">
                                                <span>W: {(e.weight * 100).toFixed(0)}%</span>
                                                <span>{e.category}</span>
                                            </div>
                                            {/* Description tooltip on hover */}
                                            {e.description && (
                                                <div className="text-[8px] text-white/15 mt-1 leading-tight opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {e.description}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ));
                })()}
            </div>

            {/* Per-Engine Contribution Breakdown */}
            <div className="glass-card p-6" data-testid="engine-contributions">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-cyan-400" />
                    <h3 className="heading-primary text-sm text-white">ENGINE CONTRIBUTION TO VERDICT</h3>
                </div>
                <div className="space-y-2">
                    {gm.all_engines?.sort((a, b) => b.weight - a.weight).map((e, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${e.agrees_with_verdict ? 'bg-green-500' : 'bg-red-500'}`} />
                            <div className="w-36 text-[10px] text-white/50 tech-mono truncate">{e.engine}</div>
                            <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{
                                    width: `${e.team_a_pct}%`,
                                    background: e.team_a_pct > 55 ? gm.team_a?.color : e.team_a_pct < 45 ? gm.team_b?.color : '#555',
                                }} />
                            </div>
                            <div className="w-12 text-right text-xs font-bold" style={{
                                color: e.team_a_pct > 55 ? gm.team_a?.color : e.team_a_pct < 45 ? gm.team_b?.color : '#888',
                            }}>{e.team_a_pct}%</div>
                            <div className="w-10 text-right text-[9px] text-white/20 tech-mono">{(e.weight * 100).toFixed(0)}%w</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* MASTER GPT-5.2 REASONING */}
            {masterGpt?.available && (
                <div className="glass-card p-6 border border-purple-500/30" data-testid="master-gpt-reasoning">
                    <div className="flex items-center gap-2 mb-4">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <span className="px-2 py-0.5 bg-purple-500/20 rounded text-[10px] text-purple-400 tech-mono font-bold">
                            MASTER GPT-5.2 — ALL-ENGINE SYNTHESIS
                        </span>
                    </div>
                    {masterGpt.verdict_statement && (
                        <div className="text-base md:text-lg text-yellow-400 font-bold mb-3 leading-snug">
                            "{masterGpt.verdict_statement}"
                        </div>
                    )}
                    <div className="text-sm text-white/70 leading-relaxed mb-4">{masterGpt.reasoning}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {masterGpt.key_factors?.length > 0 && (
                            <div className="p-3 bg-green-500/5 border border-green-500/10 rounded-lg">
                                <div className="text-[10px] text-green-400 tech-mono font-bold mb-2">KEY DECISIVE FACTORS</div>
                                {masterGpt.key_factors.map((f, i) => (
                                    <div key={i} className="text-sm text-white/60 py-0.5 flex items-start gap-2">
                                        <Star className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />{f}
                                    </div>
                                ))}
                            </div>
                        )}
                        {masterGpt.risk_factors?.length > 0 && (
                            <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
                                <div className="text-[10px] text-red-400 tech-mono font-bold mb-2">UPSET RISK FACTORS</div>
                                {masterGpt.risk_factors.map((f, i) => (
                                    <div key={i} className="text-sm text-white/60 py-0.5 flex items-start gap-2">
                                        <Shield className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />{f}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* RISK & EDGE ANALYSIS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-5" data-testid="edge-factors">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <h3 className="heading-primary text-sm text-green-400">EDGE FACTORS</h3>
                    </div>
                    {gm.edge_factors?.length > 0 ? gm.edge_factors.map((f, i) => (
                        <div key={i} className="text-sm text-white/60 py-1 flex items-start gap-2">
                            <Star className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />{f}
                        </div>
                    )) : <div className="text-sm text-white/30">No strong edges identified</div>}
                </div>
                <div className="glass-card p-5" data-testid="risk-factors">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-red-400" />
                        <h3 className="heading-primary text-sm text-red-400">RISK FACTORS</h3>
                    </div>
                    {gm.risk_factors?.length > 0 ? gm.risk_factors.map((f, i) => (
                        <div key={i} className="text-sm text-white/60 py-1 flex items-start gap-2">
                            <Shield className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />{f}
                        </div>
                    )) : <div className="text-sm text-white/30">No significant risks identified</div>}
                </div>
            </div>

            {/* SIMULATION & KEY MATCHUPS SUMMARY */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-5" data-testid="god-mode-sim-summary">
                    <div className="flex items-center gap-2 mb-3">
                        <FlaskConical className="w-4 h-4 text-purple-400" />
                        <h3 className="heading-primary text-sm text-white">SIMULATION SUMMARY</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="p-2 bg-white/5 rounded-lg">
                            <div className="text-[10px] text-white/30 tech-mono">AVG SCORE</div>
                            <div className="heading-primary text-lg" style={{ color: gm.team_a?.color }}>{gm.simulation_summary?.team_a_avg_score}</div>
                            <div className="text-[10px] text-white/20">{gm.team_a?.short_name}</div>
                        </div>
                        <div className="p-2 bg-white/5 rounded-lg">
                            <div className="text-[10px] text-white/30 tech-mono">AVG SCORE</div>
                            <div className="heading-primary text-lg" style={{ color: gm.team_b?.color }}>{gm.simulation_summary?.team_b_avg_score}</div>
                            <div className="text-[10px] text-white/20">{gm.team_b?.short_name}</div>
                        </div>
                    </div>
                    <div className="text-[10px] text-white/20 tech-mono text-center mt-2">
                        {gm.simulation_summary?.simulations?.toLocaleString()} SIMULATIONS | RANGE: {gm.simulation_summary?.team_a_score_range} vs {gm.simulation_summary?.team_b_score_range}
                    </div>
                </div>
                <div className="glass-card p-5" data-testid="god-mode-matchups">
                    <div className="flex items-center gap-2 mb-3">
                        <Swords className="w-4 h-4 text-orange-400" />
                        <h3 className="heading-primary text-sm text-white">KEY MATCHUPS</h3>
                    </div>
                    <div className="space-y-1.5">
                        {gm.key_matchups?.slice(0, 4).map((m, i) => (
                            <div key={i} className="flex items-center justify-between py-1 px-2 bg-white/5 rounded text-xs">
                                <span className="text-white/70">{m.batter} vs {m.bowler}</span>
                                <span className={m.advantage === 'Batter' ? 'text-green-400' : 'text-red-400'}>
                                    {m.advantage} +{m.advantage_percentage}%
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ━━━ INTELLIGENCE DATA SOURCES PANEL ━━━ */}
            {gm.intelligence_data && (
                <div className="glass-card p-5 border border-cyan-500/20" data-testid="god-mode-intelligence">
                    <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-4 h-4 text-cyan-400" />
                        <h3 className="heading-primary text-sm text-white">INTELLIGENCE DATA FEEDING INTO VERDICT</h3>
                        <span className="px-2 py-0.5 bg-cyan-500/20 rounded text-[9px] text-cyan-400 tech-mono">{gm.intelligence_data?.engines_count} ENGINES + {gm.intelligence_data?.total_data_sources} DATA SOURCES</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="bg-white/5 rounded-lg p-2.5">
                            <div className="text-[9px] text-white/30 tech-mono mb-1">SOCIAL SENTIMENT</div>
                            <div className="text-xs">
                                <span style={{ color: gm.team_a?.color }}>{gm.team_a?.short_name} {gm.intelligence_data?.social_sentiment?.team_a_avg > 0 ? '+' : ''}{(gm.intelligence_data?.social_sentiment?.team_a_avg || 0).toFixed(3)}</span>
                                <span className="text-white/20 mx-1">vs</span>
                                <span style={{ color: gm.team_b?.color }}>{gm.team_b?.short_name} {gm.intelligence_data?.social_sentiment?.team_b_avg > 0 ? '+' : ''}{(gm.intelligence_data?.social_sentiment?.team_b_avg || 0).toFixed(3)}</span>
                            </div>
                            <div className="text-[8px] text-white/15 mt-0.5">{gm.intelligence_data?.social_sentiment?.total_items || 0} items analyzed</div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2.5">
                            <div className="text-[9px] text-white/30 tech-mono mb-1">GOOGLE TRENDS</div>
                            <div className="text-xs">
                                <span style={{ color: gm.team_a?.color }}>{gm.team_a?.short_name} {gm.intelligence_data?.google_trends?.team_a_interest || 0}</span>
                                <span className="text-white/20 mx-1">vs</span>
                                <span style={{ color: gm.team_b?.color }}>{gm.team_b?.short_name} {gm.intelligence_data?.google_trends?.team_b_interest || 0}</span>
                            </div>
                            <div className="text-[8px] text-white/15 mt-0.5">Search interest (IN)</div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2.5">
                            <div className="text-[9px] text-white/30 tech-mono mb-1">IMPACT PLAYER</div>
                            <div className="text-xs">
                                <span style={{ color: gm.team_a?.color }}>{gm.intelligence_data?.impact_player?.team_a?.best || 'N/A'} +{(gm.intelligence_data?.impact_player?.team_a?.boost || 0).toFixed(1)}%</span>
                            </div>
                            <div className="text-xs mt-0.5">
                                <span style={{ color: gm.team_b?.color }}>{gm.intelligence_data?.impact_player?.team_b?.best || 'N/A'} +{(gm.intelligence_data?.impact_player?.team_b?.boost || 0).toFixed(1)}%</span>
                            </div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2.5">
                            <div className="text-[9px] text-white/30 tech-mono mb-1">FANTASY XI</div>
                            <div className="text-xs">
                                <span style={{ color: gm.team_a?.color }}>{gm.team_a?.short_name} {(gm.intelligence_data?.fantasy_xi?.team_a_score || 0).toFixed(1)}</span>
                                <span className="text-white/20 mx-1">vs</span>
                                <span style={{ color: gm.team_b?.color }}>{gm.team_b?.short_name} {(gm.intelligence_data?.fantasy_xi?.team_b_score || 0).toFixed(1)}</span>
                            </div>
                            <div className="text-[8px] text-white/15 mt-0.5">Best XI strength score</div>
                        </div>
                    </div>
                    {gm.intelligence_data?.reddit?.posts > 0 && (
                        <div className="mt-2 bg-white/5 rounded-lg p-2.5">
                            <div className="text-[9px] text-white/30 tech-mono mb-1">REDDIT r/CRICKET INTELLIGENCE ({gm.intelligence_data.reddit.posts} posts)</div>
                            {gm.intelligence_data.reddit.top_insights?.map((ins, i) => (
                                <div key={i} className="text-[10px] text-white/40 mt-0.5 truncate">"{ins.text}" <span className={`${ins.score > 0 ? 'text-green-400' : ins.score < 0 ? 'text-red-400' : 'text-white/20'}`}>({ins.score > 0 ? '+' : ''}{ins.score?.toFixed(2)})</span></div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="text-center text-[10px] text-white/15 tech-mono">
                GOD MODE v2.0 — {gm.all_engines?.length} ENGINES | {gm.intelligence_data?.total_data_sources || 0} DATA SOURCES | {gm.simulation_summary?.simulations?.toLocaleString()} SIMULATIONS | GPT-5.2 MASTER REASONING | {gm.generated_at?.split('T')[0]}
            </div>
        </div>
    );
}

/* Shared helpers imported from components/dashboard/SharedComponents */

